import React from 'react';
import { MapPin, Leaf, Palmtree, Waves } from 'lucide-react';
import SEOSchema from '../components/SEOSchema';

const GardenPage = () => {
  return (
    <div className="pt-20 stone-texture" data-testid="garden-page">
      {/* Page-specific SEO via SEOSchema component */}
      <SEOSchema page="garden" />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center" data-testid="garden-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://a0.muscache.com/im/pictures/hosting/Hosting-36517649/original/3122c74f-c395-4948-a348-b16f97f68341.jpeg?im_w=1200')",
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl">
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold drop-shadow-lg" data-testid="garden-title">
            The Renaissance Garden
          </h1>
          <p className="mt-4 text-lg sm:text-xl drop-shadow-md">
            A 16th-century masterpiece of garden architecture on the Adriatic coast
          </p>
        </div>
      </section>

      {/* Rich SEO Content Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="text-lg leading-relaxed" data-testid="seo-intro">
              <strong>Renaissance Garden Dubrovnik</strong> features one of the most remarkable historic gardens 
              in the <strong>Dubrovnik-Neretva region</strong> of Croatia. Dating from the <strong>16th century</strong>, 
              during the golden age of the <strong>Dubrovnik Republic</strong> (Ragusa), this <strong>1000 square meter</strong> garden 
              represents the pinnacle of <strong>Renaissance period</strong> landscape design. The garden's original 
              <strong> stone pathways</strong>, <strong>heritage walls</strong>, and <strong>vine-covered walkways</strong> have 
              been meticulously preserved, offering visitors an authentic glimpse into Dubrovnik's aristocratic past.
            </p>
            <p className="text-lg leading-relaxed mt-4" data-testid="seo-details">
              Located on the <strong>waterfront</strong> in Mokošica, the garden combines traditional 
              <strong> Mediterranean plants</strong>—including citrus trees, oleanders, lavender, and ancient olive trees—with 
              Renaissance symmetry and design principles. The property's direct <strong>Adriatic Sea</strong> access 
              and panoramic views of the <strong>Dubrovnik coastline</strong> make it one of the most photographed 
              historic gardens in southern Croatia.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div data-testid="garden-intro">
                <h2 className="font-playfair text-3xl font-bold text-primary mb-6">
                  A Living Monument to History
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  The garden of Villa Renaissance is not just a beautiful outdoor space—it is a living historical
                  monument. Spanning 1000 m², this meticulously preserved Renaissance garden represents the
                  pinnacle of Dubrovnik's aristocratic summer residence tradition, established during the 
                  height of the Dubrovnik Republic in the 16th century.
                </p>
              </div>

              <div data-testid="garden-heritage">
                <h3 className="font-playfair text-2xl font-semibold mb-4">Historic Preservation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Among the best-preserved Renaissance gardens in the entire Dubrovnik region, our garden has been
                  carefully maintained to honor its 16th-century origins while providing modern comfort for
                  discerning guests. The original stone pathways wind through carefully cultivated Mediterranean 
                  plants, beneath vine-covered pergolas that have shaded visitors for over four centuries.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6" data-testid="garden-features">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Leaf className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Heritage Walls</h4>
                    <p className="text-sm text-muted-foreground">Original stone walls from the 16th century Dubrovnik Republic</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Palmtree className="text-secondary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Vine Walkways</h4>
                    <p className="text-sm text-muted-foreground">Shaded vine-covered paths through Mediterranean plantings</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Waves className="text-primary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Waterfront Location</h4>
                    <p className="text-sm text-muted-foreground">Direct Adriatic Sea access with private boat mooring</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-secondary" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">1000 m² Garden</h4>
                    <p className="text-sm text-muted-foreground">Expansive Renaissance garden space</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <img
                src="https://a0.muscache.com/im/pictures/hosting/Hosting-36517649/original/98fddc17-f01f-4d53-be28-9e42a99bc5a9.jpeg?im_w=1200"
                alt="16th century stone pathway through Renaissance garden in Dubrovnik with Mediterranean plants"
                className="rounded-none shadow-lg w-full"
                data-testid="garden-path-image"
              />
              <img
                src="https://a0.muscache.com/im/pictures/caeec492-61c4-4449-a4d8-ff805faa06a2.jpg?im_w=1200"
                alt="Vine-covered walkway with heritage walls in historic Dubrovnik garden"
                className="rounded-none shadow-lg w-full"
                data-testid="garden-view-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Full Width Image Section */}
      <section className="py-0" data-testid="garden-full-width">
        <img
          src="https://a0.muscache.com/im/pictures/cb08b587-d389-47ca-ac66-2b1d0e86f80e.jpg?im_w=1200"
          alt="Terrace overlooking Adriatic Sea from Renaissance Garden Dubrovnik"
          className="w-full h-96 object-cover"
          data-testid="terrace-image"
        />
      </section>

      {/* Details Section */}
      <section className="py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-playfair text-3xl font-bold text-center mb-16" data-testid="details-heading">
            Experience Dubrovnik's Stone Heritage
          </h2>
          <div className="prose prose-lg max-w-3xl mx-auto text-muted-foreground">
            <p data-testid="details-text-1">
              Dubrovnik is known worldwide as the <strong>"Pearl of the Adriatic"</strong> and the 
              <strong> "City made of Stone."</strong> Our villa embodies this heritage with its original 
              Renaissance stone architecture and carefully preserved garden walls dating from the era of 
              the Dubrovnik Republic.
            </p>
            <p data-testid="details-text-2">
              The garden was designed in the <strong>16th century</strong> as a summer retreat for Dubrovnik's nobility, 
              featuring the sophisticated layout and <strong>Mediterranean plantings</strong> characteristic of the 
              <strong> Renaissance period</strong>. Today, it offers guests a unique opportunity to experience 
              this historical legacy firsthand while enjoying modern amenities including a heated swimming pool.
            </p>
            <p data-testid="details-text-3">
              Walk the <strong>vine-covered stone pathways</strong>, relax by the pool surrounded by ancient walls, 
              and feel the timeless beauty that has drawn visitors to this special <strong>waterfront location</strong> 
              for over 400 years. The garden's position on the Adriatic coast provides stunning sea views and 
              direct water access—a rare combination in historic Dubrovnik properties.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GardenPage;
